<template>
  <div class="soar-container flex gap-3 flex-col">
    <CollapsibleSection
      :key="key"
      :header-label="$t(section.header)"
      body-class="w-full flex flex-wrap "
      v-for="(section, key) in dataList"
    >
      <div
        :key="key"
        v-for="(row, key) in section.sectionData"
        class="w-full md:w-1/2 flex flex-row gap-x-2"
      >
        <span>{{ $t(row.label) }} :</span>
        <span class="text-gray-600">{{ row.value }}</span>
      </div>
    </CollapsibleSection>
    <CollapsibleSection :header-label="$t('Accompanies')">
      <VisaViewTable
        :tableHeaders="tableHeaders"
        :tableKeyList="tableKeyList"
        :tableData="tableData"
      />
    </CollapsibleSection>
  </div>
</template>
<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { DateTime } from 'luxon'
import CollapsibleSection from '@/components/Ui/CollapsibleSection.vue'
import VisaViewTable from '@/views/individual/sponsored/VisaViewTable.vue'

import apiHelperService from '@/services'

import { useI18n } from 'vue-i18n'

const dataList = ref([])
const tableData = ref([])
const route = useRoute()
const i18n = useI18n()

const defaultDateFormat = 'yyyy/MM/dd'

const formatVisaType = (request) => {
  return `${request.module?.text} - ${request.moduleService?.text} - ${request.subModuleService?.text}`
}
const formatFileResidencyNumber = (request) => {
  return `${request.departmentCode}/${request.serviceYear}/${request.serviceCode}/${request.sequenceNumber}`
}
const formatDate = (val) => {
  return val ? DateTime.fromISO(val).toFormat(defaultDateFormat) : ''
}

const fetchData = async () => {
  let request = await apiHelperService.get(`/api/establishment/visa/${route.query.fileNumber}`)
  tableData.value = request.accompanies
  dataList.value = [
    {
      header: 'visaInformation',
      sectionData: [
        {
          label: 'VisaType',
          value: formatVisaType(request)
        },
        {
          label: 'fileVisaNumber',
          value: formatFileResidencyNumber(request)
        },
        {
          label: 'visaIssueDate',
          value: formatDate(request.fileIssueDate)
        },
        {
          label: 'visaValidityDate',
          value: formatDate(request.fileValidityDate)
        },
        {
          label: 'visaExpireDate',
          value: formatDate(request.fileExpireDate)
        },
        {
          label: 'visaStatus',
          value: request.serviceStatus?.text
        }
      ]
    },
    {
      header: 'personalInfo',
      sectionData: [
        {
          label: 'unifiedNumber',
          value: request.personUnifiedNumber
        },
        {
          label: 'IdentityNumber',
          value: request.identityNumber
        },
        {
          label: 'EnglishFullName',
          value: request.englishName
        },
        {
          label: 'ArabicFullName',
          value: request.arabicName
        },
        {
          label: 'CurrentNationality',
          value: request.currentNationality?.text
        },
        {
          label: 'PreviousNationality',
          value: request.previousNationality?.text
        },
        {
          label: 'Gender',
          value: request.gender?.text
        },
        {
          label: 'DateOfBirth',
          value: formatDate(request.dateOfBirth)
        },
        {
          label: 'EnglishPlaceOfBirth',
          value: request.englishPlaceOfBirth
        },
        {
          label: 'ArabicPlaceOfBirth',
          value: request.arabicPlaceOfBirth
        },
        {
          label: 'MotherEnglishName',
          value: request.motherEnglishName
        },
        {
          label: 'MotherArabicName',
          value: request.motherArabicName
        },
        {
          label: 'Religion',
          value: request.religion?.text
        },
        {
          label: 'BirthCountry',
          value: request.countryOfBirth?.text
        },
        {
          label: 'Profession',
          value: request.profession?.text
        },
        {
          label: 'MaritalStatus',
          value: request.maritalStatus?.text
        },
        {
          label: 'relation',
          value: request.sponsorRelationship?.text
        }
      ]
    },
    {
      header: 'PassportInformation',
      sectionData: [
        {
          label: 'PassportTypes',
          value: request.passportType?.text
        },
        {
          label: 'PassportNo',
          value: request.passportNumber
        },
        {
          label: 'EnglishPassportIssuePlace',
          value: request.passportEnglishIssuePlace
        },
        {
          label: 'ArabicPassportIssuePlace',
          value: request.passportArabicIssuePlace
        },
        {
          label: 'PassportIssueDate',
          value: formatDate(request.passportIssueDate)
        },
        {
          label: 'PassportExpireDate',
          value: formatDate(request.passportExpiryDate)
        },
        {
          label: 'PassportCountry',
          value: request.passportCountry?.text
        }
      ]
    },
    {
      header: 'addresses',
      sectionData: [
        {
          label: 'uaelocation',
          value: request.localAddressDescription
        },
        {
          label: 'uaeMobileNumber',
          value: request.localMobile
        },
        {
          label: 'uaePhoneNumber',
          value: request.localHomePhone
        },
        {
          label: 'outsideLocation',
          value: request.abroadAddressDescription
        },
        {
          label: 'ViewMobileNumber',
          value: request.abroadPhone
        },
        {
          label: 'Email',
          value: request.emailAddress
        }
      ]
    }
  ]
}

const tableHeaders = ref([
  {
    value: 1,
    label: i18n.t('EnglishName')
  },
  {
    value: 2,
    label: i18n.t('ArabicName')
  },
  {
    value: 3,
    label: i18n.t('relation')
  },
  {
    value: 4,
    label: i18n.t('BirthCountry')
  },
  {
    value: 5,
    label: i18n.t('DateOfBirth')
  }
])

const tableKeyList = ref([
  'englishName',
  'arabicName',
  'relationship?.text',
  'birthCountry?.text',
  'dateOfBirth'
])

onMounted(() => {
  fetchData()
})
</script>
